<template>
    <div class="account-info-page text-white" style="background-color: #292D7D; min-height: 100vh; padding: 20px;">
        <div class="container">
            <!-- Header con avatar e nome -->
            <div class="user-header text-center mb-5">
                <div class="avatar-container mb-3">
                    <img 
                        v-if="userData.avatarUrl" 
                        :src="userData.avatarUrl" 
                        alt="Avatar" 
                        class="user-avatar"
                    />
                    <div v-else class="user-avatar-placeholder">
                        <i class="fa fa-user" style="font-size: 48px;"></i>
                    </div>
                </div>
                <h1 class="user-name mb-0">{{ userFullName }}</h1>
            </div>

            <!-- Messaggio di successo/errore -->
            <div v-if="successMessage" class="alert alert-success" role="alert">
                <i class="fa fa-check-circle"></i> {{ successMessage }}
            </div>
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
                <i class="fa fa-exclamation-circle"></i> {{ errorMessage }}
            </div>

            <!-- Sezione Account (Email) -->
            <div class="section-card mb-4">
                <h3 class="section-title mb-4">
                    <i class="fa fa-envelope me-2"></i>Account
                </h3>
                <div class="row">
                    <div class="col-12 col-md-8">
                        <div class="form-group">
                            <label class="form-label">Email attuale</label>
                            <input 
                                type="email" 
                                :value="userData.email" 
                                class="form-control" 
                                disabled
                                readonly
                            />
                        </div>
                    </div>
                    <div class="col-12 col-md-4 d-flex align-items-end">
                        <button 
                            type="button" 
                            class="btn btn-custom w-100" 
                            @click="openEmailChangeModal"
                            :disabled="isLoading"
                        >
                            <i class="fa fa-edit me-2"></i>Modifica email
                        </button>
                    </div>
                    </div>
                </div>

            <form @submit.prevent="confirmAndSaveChanges">

                <!-- Sezione Informazioni Personali -->
                <div class="section-card mb-4">
                    <h3 class="section-title mb-4">
                        <i class="fa fa-user me-2"></i>Informazioni Personali
                    </h3>
                    <div v-if="documentsConfirmed" class="mb-3">
                        <div class="alert alert-warning" role="alert">
                            <i class="fa fa-lock me-2"></i>
                            <strong>Informazioni bloccate:</strong> Non è possibile modificare queste informazioni dopo la verifica dei documenti.
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Nome *</label>
                                <input 
                                    v-model="formData.personalInfo.firstName" 
                                    type="text" 
                                    placeholder="Nome" 
                                    class="form-control"
                                    :disabled="documentsConfirmed"
                                    required
                                    @input="handleNameInput('personalInfo', 'firstName', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Cognome *</label>
                                <input 
                                    v-model="formData.personalInfo.lastName" 
                                    type="text" 
                                    placeholder="Cognome" 
                                    class="form-control"
                                    :disabled="documentsConfirmed"
                                    required
                                    @input="handleNameInput('personalInfo', 'lastName', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Data di nascita *</label>
                                <Datepicker 
                                    v-model="formData.personalInfo.dateOfBirth" 
                                    :format="format" 
                                    :placeholder="'Data di nascita'"
                                    :enable-time-picker="false" 
                                    :auto-apply="true" 
                                    :teleport="true"
                                    :disabled="documentsConfirmed"
                                    input-class-name="form-control" 
                                    :preview-format="previewFormat"
                                    required
                                >
                                    <template #action-select>
                                        <span class="action-select">Seleziona</span>
                                    </template>
                                </Datepicker>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Luogo di nascita *</label>
                                <input 
                                    v-model="formData.personalInfo.placeOfBirth" 
                                    type="text" 
                                    placeholder="Luogo di nascita" 
                                    class="form-control"
                                    :disabled="documentsConfirmed"
                                    required
                                    @input="handleUppercaseInput('personalInfo', 'placeOfBirth', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Codice Fiscale *</label>
                                <input 
                                    v-model="formData.personalInfo.taxCode" 
                                    type="text" 
                                    placeholder="Codice Fiscale" 
                                    class="form-control"
                                    :disabled="documentsConfirmed"
                                    required
                                    @input="handleUppercaseInput('personalInfo', 'taxCode', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Genere *</label>
                                <GenderSelect 
                                    v-model="formData.personalInfo.gender"
                                    :disabled="documentsConfirmed"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sezione Contatti -->
                <div class="section-card mb-4">
                    <h3 class="section-title mb-4">
                        <i class="fa fa-address-book me-2"></i>Contatti
                    </h3>
                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group">
                                <label class="form-label">Indirizzo *</label>
                                <input 
                                    v-model="formData.contactInfo.address" 
                                    type="text" 
                                    placeholder="Indirizzo" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'address', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Città *</label>
                                <input 
                                    v-model="formData.contactInfo.city" 
                                    type="text" 
                                    placeholder="Città" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'city', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="form-group">
                                <label class="form-label">CAP *</label>
                                <input 
                                    v-model="formData.contactInfo.postalCode" 
                                    type="text" 
                                    placeholder="CAP" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'postalCode', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="form-group">
                                <label class="form-label">Provincia *</label>
                                <input 
                                    v-model="formData.contactInfo.province" 
                                    type="text" 
                                    placeholder="Provincia" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'province', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Paese *</label>
                                <input 
                                    v-model="formData.contactInfo.country" 
                                    type="text" 
                                    placeholder="Paese" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'country', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Telefono *</label>
                                <input 
                                    v-model="formData.contactInfo.phoneNumber" 
                                    type="text" 
                                    placeholder="Telefono" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'phoneNumber', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Cellulare *</label>
                                <input 
                                    v-model="formData.contactInfo.mobileNumber" 
                                    type="text" 
                                    placeholder="Cellulare" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('contactInfo', 'mobileNumber', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sezione Tutore Legale (solo se minorenne) -->
                <div v-if="isMinor" class="section-card mb-4">
                    <h3 class="section-title mb-4">
                        <i class="fa fa-shield me-2"></i>Tutore Legale
                    </h3>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Nome *</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.firstName" 
                                    type="text" 
                                    placeholder="Nome tutore" 
                                    class="form-control"
                                    required
                                    @input="handleNameInput('legalGuardian', 'firstName', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Cognome *</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.lastName" 
                                    type="text" 
                                    placeholder="Cognome tutore" 
                                    class="form-control"
                                    required
                                    @input="handleNameInput('legalGuardian', 'lastName', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Codice Fiscale *</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.taxCode" 
                                    type="text" 
                                    placeholder="Codice Fiscale tutore" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('legalGuardianInfo', 'taxCode', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Relazione *</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.relationship" 
                                    type="text" 
                                    placeholder="Relazione con il minore (es. Genitore)" 
                                    class="form-control"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Email *</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.email" 
                                    type="email" 
                                    placeholder="Email tutore" 
                                    class="form-control"
                                    required
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Telefono *</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.phoneNumber" 
                                    type="text" 
                                    placeholder="Telefono tutore" 
                                    class="form-control"
                                    required
                                    @input="handleUppercaseInput('legalGuardianInfo', 'phoneNumber', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group">
                                <label class="form-label">Indirizzo</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.address" 
                                    type="text" 
                                    placeholder="Indirizzo tutore" 
                                    class="form-control"
                                    @input="handleUppercaseInput('legalGuardianInfo', 'address', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">Città</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.city" 
                                    type="text" 
                                    placeholder="Città tutore" 
                                    class="form-control"
                                    @input="handleUppercaseInput('legalGuardianInfo', 'city', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="form-label">CAP</label>
                                <input 
                                    v-model="formData.legalGuardianInfo.postalCode" 
                                    type="text" 
                                    placeholder="CAP tutore" 
                                    class="form-control"
                                    @input="handleUppercaseInput('legalGuardianInfo', 'postalCode', $event)"
                                    style="text-transform: uppercase;"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pulsante Salva -->
                <div class="text-center mt-4 mb-5">
                    <button 
                        type="submit" 
                        class="btn btn-custom" 
                        :disabled="isLoading"
                        :class="{ 'loading': isLoading }"
                    >
                        <span v-if="!isLoading">
                            <i class="fa fa-save me-2"></i>Salva modifiche
                        </span>
                        <span v-else>
                            <span class="loading-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </span>
                        </span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import GenderSelect from '@/components/GenderSelect.vue'
import Swal from 'sweetalert2'

export default defineComponent({
    name: 'AccountInfoPage',
    components: {
        Datepicker,
        GenderSelect
    },
    data() {
        return {
            userToken: null,
            userData: {
                id: null,
                email: '',
                avatarUrl: null,
                personalInfo: {
                    firstName: '',
                    lastName: '',
                    dateOfBirth: null,
                    placeOfBirth: '',
                    taxCode: '',
                    gender: ''
                },
                contactInfo: {
                    address: '',
                    city: '',
                    postalCode: '',
                    province: '',
                    country: '',
                    phoneNumber: '',
                    mobileNumber: ''
                },
                legalGuardianInfo: null
            },
            formData: {
                personalInfo: {
                    firstName: '',
                    lastName: '',
                    dateOfBirth: null,
                    placeOfBirth: '',
                    taxCode: '',
                    gender: ''
                },
                contactInfo: {
                    address: '',
                    city: '',
                    postalCode: '',
                    province: '',
                    country: '',
                    phoneNumber: '',
                    mobileNumber: ''
                },
                legalGuardianInfo: {
                    firstName: '',
                    lastName: '',
                    taxCode: '',
                    relationship: '',
                    email: '',
                    phoneNumber: '',
                    address: '',
                    city: '',
                    postalCode: ''
                }
            },
            isLoading: false,
            successMessage: '',
            errorMessage: '',
            documentsConfirmed: false,
            format: 'dd/MM/yyyy',
            previewFormat: (date) => {
                if (!date) return ''
                const day = date.getDate().toString().padStart(2, '0')
                const month = (date.getMonth() + 1).toString().padStart(2, '0')
                const year = date.getFullYear()
                return `${day}/${month}/${year}`
            }
        }
    },
    computed: {
        userFullName() {
            const firstName = this.userData.personalInfo?.firstName || ''
            const lastName = this.userData.personalInfo?.lastName || ''
            if (firstName && lastName) {
                return `${firstName} ${lastName}`.replace(/\b\w/g, c => c.toUpperCase())
            }
            return firstName || lastName || 'Utente'
        },
        isMinor() {
            if (!this.userData.personalInfo?.dateOfBirth) return false
            const birthDate = new Date(this.userData.personalInfo.dateOfBirth)
            const today = new Date()
            const age = today.getFullYear() - birthDate.getFullYear()
            const monthDiff = today.getMonth() - birthDate.getMonth()
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                return age - 1 < 18
            }
            return age < 18
        },
        hasLegalGuardian() {
            return this.userData.legalGuardianInfo != null
        }
    },
    async created() {
        // Verifica se l'utente è loggato
        var token = localStorage.getItem('userToken')

        // Verifica se c'è il token da URL
        const userTokenFromUrl = this.$route.query?.userToken
        if (userTokenFromUrl) {
            // Verifica che il token dall'URL sia valido
            try {
                const response = await fetch('https://api.elitefitness.it/api/users/profile', {
                    headers: {
                        Authorization: `Bearer ${userTokenFromUrl}`,
                    },
                })
                if (response.ok) {
                    token = userTokenFromUrl
                }
            } catch (error) {
                console.error('Errore nella verifica del token dall\'URL:', error)
            }
        }

        if (!token) {
            // Nessun token trovato: redirect alla login con returnUrl
            const returnUrl = encodeURIComponent(this.$route.fullPath)
            this.$router.push({ 
                path: '/login', 
                query: { returnUrl } 
            })
            return
        }

        this.userToken = token

        // Salva il token in localStorage se proviene dall'URL
        if (userTokenFromUrl && userTokenFromUrl === token) {
            localStorage.setItem('userToken', token)
        }

        await this.loadUserData()
        await this.loadDocumentsStatus()
    },
    methods: {
        async loadUserData() {
            try {
                const response = await fetch('https://api.elitefitness.it/api/users/profile', {
                    headers: {
                        'Authorization': `Bearer ${this.userToken}`
                    }
                })

                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        // Pulisci i token salvati
                        localStorage.removeItem('userToken')
                        localStorage.removeItem('userEmail')
                        localStorage.removeItem('tokenExpiration')
                        
                        // Reindirizza al login con returnUrl
                        const returnUrl = encodeURIComponent(this.$route.fullPath)
                        this.$router.push({ 
                            path: '/login', 
                            query: { returnUrl } 
                        })
                        return
                    }
                    throw new Error('Errore nel caricamento dei dati utente')
                }

                const data = await response.json()
                
                // Converte la data di nascita dal formato stringa al Date object
                let dateOfBirth = null
                if (data.personalInfo?.dateOfBirth) {
                    try {
                        // Gestisce formato "dd-MM-yyyy" o ISO
                        const dateStr = data.personalInfo.dateOfBirth
                        if (dateStr.includes('-')) {
                            const parts = dateStr.split('-')
                            if (parts.length === 3) {
                                // Formato dd-MM-yyyy
                                if (parts[0].length === 2) {
                                    dateOfBirth = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`)
                                } else {
                                    // Formato yyyy-MM-dd
                                    dateOfBirth = new Date(dateStr)
                                }
                            } else {
                                dateOfBirth = new Date(dateStr)
                            }
                        } else {
                            dateOfBirth = new Date(dateStr)
                        }
                        
                        // Verifica che la data sia valida
                        if (isNaN(dateOfBirth.getTime())) {
                            dateOfBirth = null
                        }
                    } catch (e) {
                        console.error('Errore nella conversione della data:', e)
                        dateOfBirth = null
                    }
                }
                
                this.userData = {
                    id: data.id,
                    email: data.email || '',
                    avatarUrl: data.avatarUrl || null,
                    personalInfo: {
                        firstName: data.personalInfo?.firstName || '',
                        lastName: data.personalInfo?.lastName || '',
                        dateOfBirth: dateOfBirth,
                        placeOfBirth: data.personalInfo?.placeOfBirth || '',
                        taxCode: data.personalInfo?.taxCode || '',
                        gender: data.personalInfo?.gender || ''
                    },
                    contactInfo: {
                        address: data.contactInfo?.address || '',
                        city: data.contactInfo?.city || '',
                        postalCode: data.contactInfo?.postalCode || '',
                        province: data.contactInfo?.province || '',
                        country: data.contactInfo?.country || '',
                        phoneNumber: data.contactInfo?.phoneNumber || '',
                        mobileNumber: data.contactInfo?.mobileNumber || ''
                    },
                    legalGuardianInfo: data.legalGuardianInfo || null
                }

                // Popola il form con i dati esistenti
                this.formData.personalInfo = { ...this.userData.personalInfo }
                this.formData.contactInfo = { ...this.userData.contactInfo }
                // Inizializza Legal Guardian Info (se minorenne, mostra sempre i campi, anche se vuoti)
                if (this.userData.legalGuardianInfo) {
                    this.formData.legalGuardianInfo = {
                        firstName: this.userData.legalGuardianInfo.firstName || '',
                        lastName: this.userData.legalGuardianInfo.lastName || '',
                        taxCode: this.userData.legalGuardianInfo.taxCode || '',
                        relationship: this.userData.legalGuardianInfo.relationship || '',
                        email: this.userData.legalGuardianInfo.email || '',
                        phoneNumber: this.userData.legalGuardianInfo.phoneNumber || '',
                        address: this.userData.legalGuardianInfo.address || '',
                        city: this.userData.legalGuardianInfo.city || '',
                        postalCode: this.userData.legalGuardianInfo.postalCode || ''
                    }
                } else {
                    // Se minorenne ma non ha Legal Guardian, inizializza con valori vuoti
                    this.formData.legalGuardianInfo = {
                        firstName: '',
                        lastName: '',
                        taxCode: '',
                        relationship: '',
                        email: '',
                        phoneNumber: '',
                        address: '',
                        city: '',
                        postalCode: ''
                    }
                }

            } catch (error) {
                console.error('Errore nel caricamento dati utente:', error)
                
                // Se il token è invalido o scaduto, reindirizza al login
                if (error.message && (error.message.includes('Token non valido') || error.message.includes('scaduto'))) {
                    // Pulisci i token salvati
                    localStorage.removeItem('userToken')
                    localStorage.removeItem('userEmail')
                    localStorage.removeItem('tokenExpiration')
                    
                    // Reindirizza al login con returnUrl
                    const returnUrl = encodeURIComponent(this.$route.fullPath)
                    this.$router.push({ 
                        path: '/login', 
                        query: { returnUrl } 
                    })
                    return
                }
                
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: error.message || 'Impossibile caricare i dati utente',
                    confirmButtonColor: '#e83539'
                })
            }
        },
        async loadDocumentsStatus() {
            if (!this.userData.id) return

            try {
                const response = await fetch(
                    `https://api.elitefitness.it/api/users/${this.userData.id}/documents/status`,
                    {
                        headers: {
                            'Authorization': `Bearer ${this.userToken}`
                        }
                    }
                )

                if (response.ok) {
                    const data = await response.json()
                    this.documentsConfirmed = data.documentsConfirmed || false
                } else {
                    console.error('Errore nel caricamento dello stato dei documenti')
                }
            } catch (error) {
                console.error('Errore nel caricamento dello stato dei documenti:', error)
            }
        },
        confirmAndSaveChanges() {
            // Verifica se ci sono modifiche da salvare
            const hasPersonalChanges = this.hasPersonalInfoChanges()
            const hasContactChanges = this.hasContactInfoChanges()
            const hasLegalGuardianChanges = this.hasLegalGuardianInfoChanges()

            if (!hasPersonalChanges && !hasContactChanges && !hasLegalGuardianChanges) {
                Swal.fire({
                    icon: 'info',
                    title: 'Nessuna modifica',
                    text: 'Non ci sono modifiche da salvare',
                    confirmButtonColor: '#e83539'
                })
                return
            }

            // Prepara il messaggio con le modifiche che verranno applicate
            const changesList = []
            if (hasPersonalChanges) {
                changesList.push('Informazioni personali')
            }
            if (hasContactChanges) {
                changesList.push('Informazioni di contatto')
            }
            if (hasLegalGuardianChanges) {
                changesList.push('Informazioni del tutore legale')
            }

            const changesText = changesList.length > 0 
                ? `<strong>Le seguenti modifiche verranno applicate:</strong><br>• ${changesList.join('<br>• ')}`
                : 'Le modifiche verranno applicate'

            // Mostra il dialog di conferma
            Swal.fire({
                icon: 'question',
                title: 'Conferma modifiche',
                html: `${changesText}<br><br>Sei sicuro di voler salvare queste modifiche?`,
                showCancelButton: true,
                confirmButtonText: 'Conferma e salva',
                cancelButtonText: 'Annulla',
                confirmButtonColor: '#e83539',
                cancelButtonColor: 'transparent',
                customClass: {
                    popup: 'swal2-popup',
                    title: 'swal2-title',
                    content: 'swal2-content',
                    confirmButton: 'swal2-confirm',
                    cancelButton: 'swal2-cancel'
                },
                buttonsStyling: true,
                reverseButtons: true
            }).then((result) => {
                if (result.isConfirmed) {
                    this.saveChanges()
                }
            })
        },
        async saveChanges() {
            this.isLoading = true
            this.successMessage = ''
            this.errorMessage = ''

            try {
                // Validazione campi obbligatori
                const validationErrors = []
                
                if (!this.formData.personalInfo.firstName || this.formData.personalInfo.firstName.trim() === '') {
                    validationErrors.push('Il nome è obbligatorio')
                }
                if (!this.formData.personalInfo.lastName || this.formData.personalInfo.lastName.trim() === '') {
                    validationErrors.push('Il cognome è obbligatorio')
                }
                if (!this.formData.personalInfo.dateOfBirth) {
                    validationErrors.push('La data di nascita è obbligatoria')
                }
                if (!this.formData.personalInfo.placeOfBirth || this.formData.personalInfo.placeOfBirth.trim() === '') {
                    validationErrors.push('Il luogo di nascita è obbligatorio')
                }
                if (!this.formData.personalInfo.taxCode || this.formData.personalInfo.taxCode.trim() === '') {
                    validationErrors.push('Il codice fiscale è obbligatorio')
                }
                if (!this.formData.personalInfo.gender || this.formData.personalInfo.gender.trim() === '') {
                    validationErrors.push('Il genere è obbligatorio')
                }
                if (!this.formData.contactInfo.address || this.formData.contactInfo.address.trim() === '') {
                    validationErrors.push('L\'indirizzo è obbligatorio')
                }
                if (!this.formData.contactInfo.city || this.formData.contactInfo.city.trim() === '') {
                    validationErrors.push('La città è obbligatoria')
                }
                if (!this.formData.contactInfo.postalCode || this.formData.contactInfo.postalCode.trim() === '') {
                    validationErrors.push('Il CAP è obbligatorio')
                }
                if (!this.formData.contactInfo.province || this.formData.contactInfo.province.trim() === '') {
                    validationErrors.push('La provincia è obbligatoria')
                }
                if (!this.formData.contactInfo.country || this.formData.contactInfo.country.trim() === '') {
                    validationErrors.push('Il paese è obbligatorio')
                }
                if (!this.formData.contactInfo.phoneNumber || this.formData.contactInfo.phoneNumber.trim() === '') {
                    validationErrors.push('Il telefono è obbligatorio')
                }
                if (!this.formData.contactInfo.mobileNumber || this.formData.contactInfo.mobileNumber.trim() === '') {
                    validationErrors.push('Il cellulare è obbligatorio')
                }

                // Validazione campi obbligatori legal guardian (solo se minorenne)
                if (this.isMinor) {
                    if (!this.formData.legalGuardianInfo.firstName || this.formData.legalGuardianInfo.firstName.trim() === '') {
                        validationErrors.push('Il nome del tutore è obbligatorio')
                    }
                    if (!this.formData.legalGuardianInfo.lastName || this.formData.legalGuardianInfo.lastName.trim() === '') {
                        validationErrors.push('Il cognome del tutore è obbligatorio')
                    }
                    if (!this.formData.legalGuardianInfo.taxCode || this.formData.legalGuardianInfo.taxCode.trim() === '') {
                        validationErrors.push('Il codice fiscale del tutore è obbligatorio')
                    }
                    if (!this.formData.legalGuardianInfo.relationship || this.formData.legalGuardianInfo.relationship.trim() === '') {
                        validationErrors.push('La relazione con il minore è obbligatoria')
                    }
                    if (!this.formData.legalGuardianInfo.email || this.formData.legalGuardianInfo.email.trim() === '') {
                        validationErrors.push('L\'email del tutore è obbligatoria')
                    }
                    if (!this.formData.legalGuardianInfo.phoneNumber || this.formData.legalGuardianInfo.phoneNumber.trim() === '') {
                        validationErrors.push('Il telefono del tutore è obbligatorio')
                    }
                }

                if (validationErrors.length > 0) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Campi obbligatori mancanti',
                        html: validationErrors.join('<br>'),
                        confirmButtonColor: '#e83539'
                    })
                    this.isLoading = false
                    return
                }

                const updates = []

                // Gestione modifica dati utente (personal info + contact info)
                const hasPersonalChanges = this.hasPersonalInfoChanges()
                const hasContactChanges = this.hasContactInfoChanges()
                const hasLegalGuardianChanges = this.hasLegalGuardianInfoChanges()

                // Blocca il salvataggio delle Informazioni Personali se i documenti sono verificati
                if (hasPersonalChanges && this.documentsConfirmed) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Modifica non disponibile',
                        text: 'I tuoi documenti sono stati verificati. Le informazioni personali non possono essere modificate.',
                        confirmButtonColor: '#e83539'
                    })
                    this.isLoading = false
                    return
                }

                if (hasPersonalChanges || hasContactChanges || hasLegalGuardianChanges) {
                    const updatePayload = {
                        id: this.userData.id
                    }

                    if (hasPersonalChanges) {
                        updatePayload.personalInfo = {
                            firstName: this.formData.personalInfo.firstName.trim().toUpperCase(),
                            lastName: this.formData.personalInfo.lastName.trim().toUpperCase(),
                            dateOfBirth: this.formData.personalInfo.dateOfBirth 
                                ? this.formatDateForAPI(this.formData.personalInfo.dateOfBirth) 
                                : null,
                            placeOfBirth: this.formData.personalInfo.placeOfBirth.trim().toUpperCase(),
                            taxCode: this.formData.personalInfo.taxCode.trim().toUpperCase(),
                            gender: this.formData.personalInfo.gender
                        }
                    }

                    if (hasContactChanges) {
                        updatePayload.contactInfo = {
                            address: this.formData.contactInfo.address.trim().toUpperCase(),
                            city: this.formData.contactInfo.city.trim().toUpperCase(),
                            postalCode: this.formData.contactInfo.postalCode.trim().toUpperCase(),
                            province: this.formData.contactInfo.province.trim().toUpperCase(),
                            country: this.formData.contactInfo.country.trim().toUpperCase(),
                            phoneNumber: this.formData.contactInfo.phoneNumber.trim().toUpperCase(),
                            mobileNumber: this.formData.contactInfo.mobileNumber.trim().toUpperCase()
                        }
                    }

                    if (hasLegalGuardianChanges && this.isMinor) {
                        updatePayload.legalGuardianInfo = {
                            firstName: this.formData.legalGuardianInfo.firstName.trim().toUpperCase(),
                            lastName: this.formData.legalGuardianInfo.lastName.trim().toUpperCase(),
                            taxCode: this.formData.legalGuardianInfo.taxCode.trim().toUpperCase(),
                            relationship: this.formData.legalGuardianInfo.relationship.trim(),
                            email: this.formData.legalGuardianInfo.email.trim(),
                            phoneNumber: this.formData.legalGuardianInfo.phoneNumber.trim().toUpperCase(),
                            address: this.formData.legalGuardianInfo.address ? this.formData.legalGuardianInfo.address.trim().toUpperCase() : null,
                            city: this.formData.legalGuardianInfo.city ? this.formData.legalGuardianInfo.city.trim().toUpperCase() : null,
                            postalCode: this.formData.legalGuardianInfo.postalCode ? this.formData.legalGuardianInfo.postalCode.trim().toUpperCase() : null
                        }
                    }

                    const updateResponse = await fetch(
                        `https://api.elitefitness.it/api/users/${this.userData.id}`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${this.userToken}`
                            },
                            body: JSON.stringify(updatePayload)
                        }
                    )

                    if (updateResponse.status === 401 || updateResponse.status === 403) {
                        // Pulisci i token salvati
                        localStorage.removeItem('userToken')
                        localStorage.removeItem('userEmail')
                        localStorage.removeItem('tokenExpiration')
                        
                        // Reindirizza al login con returnUrl
                        const returnUrl = encodeURIComponent(this.$route.fullPath)
                        this.$router.push({ 
                            path: '/login', 
                            query: { returnUrl } 
                        })
                        return
                    }

                    if (!updateResponse.ok) {
                        const errorText = await updateResponse.text()
                        throw new Error(errorText || 'Errore nell\'aggiornamento dei dati')
                    }

                    const updatedUser = await updateResponse.json()
                    
                    // Aggiorna i dati locali
                    this.userData = {
                        ...this.userData,
                        ...updatedUser
                    }

                    if (hasPersonalChanges) {
                        updates.push('Informazioni personali aggiornate')
                    }
                    if (hasContactChanges) {
                        updates.push('Informazioni di contatto aggiornate')
                    }
                    if (hasLegalGuardianChanges) {
                        updates.push('Informazioni del tutore legale aggiornate')
                    }
                }

                if (updates.length === 0) {
                    this.errorMessage = 'Nessuna modifica da salvare'
                    this.isLoading = false
                    return
                }

                // Mostra messaggio di successo
                this.successMessage = updates.join('. ')
                
                Swal.fire({
                    icon: 'success',
                    title: 'Modifiche salvate!',
                    text: updates.join('. '),
                    confirmButtonColor: '#e83539',
                    timer: 3000
                })

                // Ricarica i dati
                await this.loadUserData()
                await this.loadDocumentsStatus()

            } catch (error) {
                console.error('Errore nel salvataggio:', error)
                
                // Se il token è invalido o scaduto, reindirizza al login
                if (error.message && (error.message.includes('Token non valido') || error.message.includes('scaduto') || error.message.includes('401') || error.message.includes('403'))) {
                    // Pulisci i token salvati
                    localStorage.removeItem('userToken')
                    localStorage.removeItem('userEmail')
                    localStorage.removeItem('tokenExpiration')
                    
                    // Reindirizza al login con returnUrl
                    const returnUrl = encodeURIComponent(this.$route.fullPath)
                    this.$router.push({ 
                        path: '/login', 
                        query: { returnUrl } 
                    })
                    return
                }
                
                this.errorMessage = error.message || 'Errore nel salvataggio delle modifiche'
                
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: error.message || 'Impossibile salvare le modifiche',
                    confirmButtonColor: '#e83539'
                })
            } finally {
                this.isLoading = false
            }
        },
        hasPersonalInfoChanges() {
            return (
                this.formData.personalInfo.firstName !== this.userData.personalInfo.firstName ||
                this.formData.personalInfo.lastName !== this.userData.personalInfo.lastName ||
                !this.compareDates(this.formData.personalInfo.dateOfBirth, this.userData.personalInfo.dateOfBirth) ||
                this.formData.personalInfo.placeOfBirth !== this.userData.personalInfo.placeOfBirth ||
                this.formData.personalInfo.taxCode !== this.userData.personalInfo.taxCode ||
                this.formData.personalInfo.gender !== this.userData.personalInfo.gender
            )
        },
        hasContactInfoChanges() {
            return (
                this.formData.contactInfo.address !== this.userData.contactInfo.address ||
                this.formData.contactInfo.city !== this.userData.contactInfo.city ||
                this.formData.contactInfo.postalCode !== this.userData.contactInfo.postalCode ||
                this.formData.contactInfo.province !== this.userData.contactInfo.province ||
                this.formData.contactInfo.country !== this.userData.contactInfo.country ||
                this.formData.contactInfo.phoneNumber !== this.userData.contactInfo.phoneNumber ||
                this.formData.contactInfo.mobileNumber !== this.userData.contactInfo.mobileNumber
            )
        },
        hasLegalGuardianInfoChanges() {
            // Se non esiste Legal Guardian nel database, verifica se ci sono valori compilati nel form
            if (!this.userData.legalGuardianInfo) {
                // Considera che ci sono modifiche se almeno uno dei campi obbligatori è compilato
                return (
                    (this.formData.legalGuardianInfo.firstName && this.formData.legalGuardianInfo.firstName.trim() !== '') ||
                    (this.formData.legalGuardianInfo.lastName && this.formData.legalGuardianInfo.lastName.trim() !== '') ||
                    (this.formData.legalGuardianInfo.taxCode && this.formData.legalGuardianInfo.taxCode.trim() !== '') ||
                    (this.formData.legalGuardianInfo.relationship && this.formData.legalGuardianInfo.relationship.trim() !== '') ||
                    (this.formData.legalGuardianInfo.email && this.formData.legalGuardianInfo.email.trim() !== '') ||
                    (this.formData.legalGuardianInfo.phoneNumber && this.formData.legalGuardianInfo.phoneNumber.trim() !== '')
                )
            }
            // Se esiste, confronta i valori
            return (
                this.formData.legalGuardianInfo.firstName !== (this.userData.legalGuardianInfo.firstName || '') ||
                this.formData.legalGuardianInfo.lastName !== (this.userData.legalGuardianInfo.lastName || '') ||
                this.formData.legalGuardianInfo.taxCode !== (this.userData.legalGuardianInfo.taxCode || '') ||
                this.formData.legalGuardianInfo.relationship !== (this.userData.legalGuardianInfo.relationship || '') ||
                this.formData.legalGuardianInfo.email !== (this.userData.legalGuardianInfo.email || '') ||
                this.formData.legalGuardianInfo.phoneNumber !== (this.userData.legalGuardianInfo.phoneNumber || '') ||
                this.formData.legalGuardianInfo.address !== (this.userData.legalGuardianInfo.address || '') ||
                this.formData.legalGuardianInfo.city !== (this.userData.legalGuardianInfo.city || '') ||
                this.formData.legalGuardianInfo.postalCode !== (this.userData.legalGuardianInfo.postalCode || '')
            )
        },
        formatDateForAPI(date) {
            if (!date) return null
            try {
                const d = date instanceof Date ? date : new Date(date)
                if (isNaN(d.getTime())) return null
                const day = d.getDate().toString().padStart(2, '0')
                const month = (d.getMonth() + 1).toString().padStart(2, '0')
                const year = d.getFullYear()
                return `${day}-${month}-${year}`
            } catch (e) {
                console.error('Errore nella formattazione della data:', e)
                return null
            }
        },
        compareDates(date1, date2) {
            if (!date1 && !date2) return true
            if (!date1 || !date2) return false
            const d1 = date1 instanceof Date ? date1 : new Date(date1)
            const d2 = date2 instanceof Date ? date2 : new Date(date2)
            return d1.getTime() === d2.getTime()
        },
        openEmailChangeModal() {
            Swal.fire({
                title: 'Modifica Email',
                html: `
                    <div style="text-align: left; font-family: 'Montserrat', sans-serif;">
                        <div style="margin-bottom: 1.5rem;">
                            <label style="display: block; color: #4a4f85; font-size: 0.9rem; margin-bottom: 0.5rem; font-weight: 500;">
                                Email attuale
                            </label>
                            <div style="background-color: #f2f4ff; border: 1px solid #c9cfe8; padding: 12px 15px; color: #16195e; font-size: 1rem; border-radius: 12px;">
                                ${this.userData.email}
                            </div>
                        </div>
                        <div style="margin-bottom: 1rem;">
                            <label style="display: block; color: #4a4f85; font-size: 0.9rem; margin-bottom: 0.5rem; font-weight: 500;">
                                Nuova email *
                            </label>
                            <input 
                                id="swal-new-email" 
                                type="email" 
                                placeholder="Inserisci nuova email"
                                style="width: 100%; 
                                       padding: 15px; 
                                       border: 1px solid #c9cfe8; 
                                       border-radius: 12px; 
                                       font-family: 'Montserrat', sans-serif;
                                       font-size: 1rem;
                                       background-color: #fff;
                                       color: #16195e;
                                       box-sizing: border-box;
                                       transition: border-color 0.3s ease;"
                            />
                            <div id="email-error" style="color: #e83539; font-size: 0.85rem; margin-top: 0.5rem; display: none; font-weight: 500;"></div>
                        </div>
                    </div>
                `,
                icon: false,
                showCancelButton: true,
                confirmButtonText: 'Conferma',
                cancelButtonText: 'Annulla',
                confirmButtonColor: '#e83539',
                cancelButtonColor: 'transparent',
                reverseButtons: true,
                focusConfirm: false,
                customClass: {
                    popup: 'swal2-popup swal-email-modal',
                    title: 'swal2-title',
                    content: 'swal2-content',
                    confirmButton: 'swal2-confirm',
                    cancelButton: 'swal2-cancel swal-email-cancel'
                },
                buttonsStyling: true,
                preConfirm: () => {
                    const emailInput = document.getElementById('swal-new-email')
                    const errorDiv = document.getElementById('email-error')
                    const newEmail = emailInput.value.trim()
                    
                    if (!newEmail) {
                        errorDiv.textContent = 'Inserisci una nuova email'
                        errorDiv.style.display = 'block'
                        emailInput.style.borderColor = '#e83539'
                        return false
                    }
                    
                    if (newEmail === this.userData.email) {
                        errorDiv.textContent = 'La nuova email deve essere diversa dall\'attuale'
                        errorDiv.style.display = 'block'
                        emailInput.style.borderColor = '#e83539'
                        return false
                    }
                    
                    // Validazione base email
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                    if (!emailRegex.test(newEmail)) {
                        errorDiv.textContent = 'Inserisci un\'email valida'
                        errorDiv.style.display = 'block'
                        emailInput.style.borderColor = '#e83539'
                        return false
                    }
                    
                    errorDiv.style.display = 'none'
                    emailInput.style.borderColor = '#c9cfe8'
                    return newEmail
                },
                didOpen: () => {
                    const emailInput = document.getElementById('swal-new-email')
                    if (emailInput) {
                        emailInput.focus()
                        emailInput.addEventListener('input', () => {
                            const errorDiv = document.getElementById('email-error')
                            if (errorDiv) {
                                errorDiv.style.display = 'none'
                                emailInput.style.borderColor = '#c9cfe8'
                            }
                        })
                        emailInput.addEventListener('focus', () => {
                            emailInput.style.borderColor = '#e83539'
                            emailInput.style.boxShadow = '0 0 0 0.25rem rgba(232, 53, 57, 0.25)'
                        })
                        emailInput.addEventListener('blur', () => {
                            emailInput.style.boxShadow = 'none'
                            if (!emailInput.value.trim()) {
                                emailInput.style.borderColor = '#c9cfe8'
                            }
                        })
                    }
                }
            }).then(async (result) => {
                if (result.isConfirmed && result.value) {
                    await this.changeEmail(result.value)
                }
            })
        },
        async changeEmail(newEmail) {
            this.isLoading = true
            
            try {
                const emailResponse = await fetch(
                    `https://api.elitefitness.it/api/users/${this.userData.id}/email/request`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${this.userToken}`
                        },
                        body: JSON.stringify({
                            newEmail: newEmail
                        })
                    }
                )

                if (emailResponse.status === 401 || emailResponse.status === 403) {
                    // Pulisci i token salvati
                    localStorage.removeItem('userToken')
                    localStorage.removeItem('userEmail')
                    localStorage.removeItem('tokenExpiration')
                    
                    // Reindirizza al login con returnUrl
                    const returnUrl = encodeURIComponent(this.$route.fullPath)
                    this.$router.push({ 
                        path: '/login', 
                        query: { returnUrl } 
                    })
                    return
                }

                const emailResult = await emailResponse.json()
                if (!emailResult.success) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Errore',
                        text: emailResult.message || 'Errore nella modifica email',
                        confirmButtonColor: '#e83539'
                    })
                    return
                }

                // Mostra il popup di verifica email
                this.showEmailVerificationAlert()

            } catch (error) {
                console.error('Errore nella modifica email:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: error.message || 'Impossibile modificare l\'email',
                    confirmButtonColor: '#e83539'
                })
            } finally {
                this.isLoading = false
            }
        },
        showEmailVerificationAlert() {
            Swal.fire({
                title: "Ti abbiamo inviato un'email alla nuova email con un link per verificare il cambio.",
                text: 'Controlla la posta in arrivo della nuova email (e la cartella spam) e clicca sul pulsante di conferma per completare la modifica. L\'email attuale rimarrà invariata fino alla conferma.',
                icon: 'info',
                iconHtml: '<i class="fa-solid fa-envelope"></i>',
                iconColor: '#e83539',
                showConfirmButton: true,
                confirmButtonText: 'OK',
                showCancelButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                didOpen: (popup) => {
                    const iconEl = popup.querySelector('.swal2-icon')
                    if (iconEl) {
                        iconEl.style.background = '#ffffff'
                        iconEl.style.border = 'none'
                        iconEl.style.color = '#e83539'
                        iconEl.style.display = 'flex'
                        iconEl.style.alignItems = 'center'
                        iconEl.style.justifyContent = 'center'
                    }
                    const iconContent = popup.querySelector('.swal2-icon .swal2-icon-content')
                    if (iconContent) {
                        iconContent.style.fontSize = '2.4rem'
                        iconContent.style.color = '#e83539'
                    }
                },
                customClass: {
                    popup: 'swal2-popup',
                    title: 'swal2-title',
                    content: 'swal2-content'
                }
            })
        },
        handleNameInput(type, field, event) {
            // Permette solo lettere e spazi, rimuove numeri e altri caratteri
            let value = event.target.value
            // Rimuove tutto tranne lettere, spazi e caratteri accentati
            value = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
            // Converte in maiuscolo
            value = value.toUpperCase()
            // Aggiorna il valore nel formData
            if (type === 'legalGuardian') {
                this.formData.legalGuardianInfo[field] = value
            } else if (type === 'personalInfo') {
                this.formData.personalInfo[field] = value
            }
            // Aggiorna anche il valore nell'input per evitare problemi di sincronizzazione
            event.target.value = value
        },
        handleUppercaseInput(section, field, event) {
            // Converte in maiuscolo
            let value = event.target.value.toUpperCase()
            // Aggiorna il valore nel formData
            this.formData[section][field] = value
            // Aggiorna anche il valore nell'input per evitare problemi di sincronizzazione
            event.target.value = value
        }
    }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.account-info-page {
    font-family: 'Montserrat', sans-serif;
    padding-top: 2rem;
    padding-bottom: 3rem;
}

.user-header {
    margin-bottom: 3rem;
}

.avatar-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.user-avatar {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.user-avatar-placeholder {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 4px solid white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.user-name {
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin-top: 1rem;
}

.section-card {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 2rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.section-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    border-bottom: 2px solid #e83539;
    padding-bottom: 0.5rem;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-label {
    color: white;
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
}

.form-control {
    font-family: 'Montserrat', sans-serif;
    background-color: rgba(255, 255, 255, 0);
    border: 2px solid white;
    color: white;
    margin-bottom: 15px;
    height: 60px;
    padding: 0 10px;
    border-radius: 0;
}

.form-control:focus {
    background-color: rgba(255, 255, 255, 0);
    border-color: #e83539;
    color: white;
    box-shadow: 0 0 0 0.25rem rgba(232, 53, 57, 0.25);
}

.form-control::placeholder {
    color: rgba(255, 255, 255, 0.6);
}

.form-control:disabled {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
}

.btn-custom {
    font-family: 'Montserrat', sans-serif;
    background-color: #e83539;
    color: white;
    border: none;
    padding: 10px 20px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
    border-radius: 0;
    font-size: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 200px;
}

.btn-custom:hover:not(:disabled) {
    background-color: #cc2e31;
}

.btn-custom.loading {
    background-color: #cc2e31;
    cursor: not-allowed;
}

.btn-custom:disabled {
    cursor: not-allowed;
    background-color: #cc2e31;
}

.availability-info {
    padding: 0.75rem;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    font-size: 0.9rem;
}

.alert {
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.5rem;
}

.alert-success {
    background-color: rgba(40, 167, 69, 0.2);
    border: 1px solid rgba(40, 167, 69, 0.5);
    color: white;
}

.alert-danger {
    background-color: rgba(220, 53, 69, 0.2);
    border: 1px solid rgba(220, 53, 69, 0.5);
    color: white;
}

.alert-warning {
    background-color: rgba(255, 193, 7, 0.2);
    border: 1px solid rgba(255, 193, 7, 0.5);
    color: white;
}

.loading-dots {
    display: inline-flex;
    gap: 2px;
}

.loading-dots span {
    animation: blink 1.4s infinite;
}

.loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes blink {
    0%, 100% {
        opacity: 0.3;
    }
    50% {
        opacity: 1;
    }
}

/* Responsive */
@media (max-width: 767px) {
    .account-info-page {
        padding: 1rem;
        padding-top: 5rem !important;
    }

    .user-name {
        font-size: 1.8rem;
    }

    .user-avatar,
    .user-avatar-placeholder {
        width: 100px;
        height: 100px;
    }

    .section-card {
        padding: 1.5rem;
    }

    .section-title {
        font-size: 1.3rem;
    }

    .btn-custom {
        padding: 0.875rem 2rem;
        font-size: 1rem;
        width: 100%;
        min-width: unset;
    }
}

@media (max-width: 575px) {
    .account-info-page {
        padding-top: 5.5rem !important;
    }

    .user-name {
        font-size: 1.5rem;
    }

    .section-card {
        padding: 1rem;
    }

    .section-title {
        font-size: 1.1rem;
    }
}

:deep(.dp__input) {
    background-color: rgba(255, 255, 255, 0);
    border: 2px solid white;
    color: white;
    height: 60px;
    border-radius: 0;
}

:deep(.dp__input::placeholder) {
    color: rgba(255, 255, 255, 0.6);
}

:deep(.dp__input:focus) {
    border-color: #e83539;
    box-shadow: 0 0 0 0.25rem rgba(232, 53, 57, 0.25);
}

:deep(.dp__main) {
    font-family: 'Montserrat', sans-serif;
}

:deep(.dp__active) {
    background-color: #e83539 !important;
}

.action-select {
    color: #e83539;
    font-weight: bold;
}
</style>

<style>
/* Il dialogo di modifica email segue l'aspetto unico definito in
   assets/css/sweetalert.css. Le regole che stavano qui erano nate per un
   popup blu e, essendo piu' specifiche (.swal-email-modal .swal2-title batte
   .swal2-title), sopravvivevano al cambio di superficie: titolo, testo e
   pulsante di annullo restavano bianchi su bianco, cioe' invisibili.
   Resta solo cio' che distingue questo dialogo dagli altri. */
.swal-email-modal #swal-new-email::placeholder {
    color: var(--swal-ink-soft, #4a4f85) !important;
    opacity: 1;
}

.swal-email-modal #swal-new-email:focus {
    outline: none !important;
    border-color: var(--swal-blue, #2e348c) !important;
    box-shadow: none !important;
}
</style>

